import { Box } from '@zoopla-eng/zds/box';
import { ButtonLeft } from '@zoopla-eng/zds/button';
import { AlertMedium, SaveMedium } from '@zoopla-eng/zds/icons';
import {
    ADBRIDG_CLASS,
    AdvertProvider,
    AdvertSlotDfp,
    AD_UNIT_PAGE,
    AD_UNIT_ROOTS,
    DfpTargeting,
    DFP_AD_UNIT,
    getPageSectionForAdUnit,
    getPageSectionForAdUnitNew,
    AD_UNIT_PAGE_TYPE
} from '@zoopla/adverts/dfp';
import { useContainerMeasurement } from '@zoopla/dom-util';
import {
    AnalyticsTaxonomy,
    Links,
    SeoLinks
} from '@zoopla/listing-search-context';
import { SeoLinksComponent } from '@zoopla/listing-search-seo-links';
import {
    AlertPanel,
    SaveThisSearchModalWithAuth,
    trackCreateEmailAlert,
    trackSaveThisSearch,
    useSaveThisSearchModal
} from '@zoopla/listing-search/alert-panel';
import { useIsLoggedIn, useUser } from '@zoopla/user';
import {
    ANALYTICS_BASE_TAXONOMY,
    Taxonomy,
    updateUserTaxonomyKeys,
    UserTaxonomy,
    useTrackSegmentEvent
} from '@zoopla/tracking';
import { useRouter } from 'next/router';
import * as React from 'react';
import { FC, useEffect, useRef, useState } from 'react';
import { AlertCtaModal } from './AlertCtaModal';
import { QuickFilters } from './QuickFilters';
import SearchAndFiltersWrapper from './SearchAndFiltersWrapper';
import {
    innerWrapperStyle,
    searchResultsWrapperStyle,
    sectionStyle,
    sideBarStyle
} from './Layout.styles.css';

export type SearchResultsLayout = {
    dfpAdTargeting: DfpTargeting;
    seoAccordions: SeoLinks[];
    numberOfRegularListings: number;
    pageNumber?: number;
    links: Links;
    children: React.ReactNode;
    analyticsTaxonomy?: AnalyticsTaxonomy;
};

const isEmptyObj = (obj: Record<string, unknown>) =>
    Object.keys(obj).length === 0;

const SearchResultsLayout: FC<React.PropsWithChildren<SearchResultsLayout>> = ({
    dfpAdTargeting,
    seoAccordions,
    children,
    pageNumber,
    links,
    analyticsTaxonomy
}) => {
    const SKYSCRAPER_AD_HEIGHT = 600;
    const isLoggedIn = useIsLoggedIn();
    const { route, pathname } = useRouter();

    const adUnitPage = getPageSectionForAdUnit(route);
    const adUnitPageNew = getPageSectionForAdUnitNew(route);

    const [adProviderVal, updateAdProviderVal] = useState({
        showPlaceHolderContent: false,
        targeting: dfpAdTargeting,
        adBridgPage: adUnitPage,
        adBridgPageNew: adUnitPageNew,
        pageType: AD_UNIT_PAGE_TYPE.LISTINGS_SEARCH_RESULT_PAGE,
        delayInit: isEmptyObj(dfpAdTargeting)
    });

    useEffect(() => {
        updateAdProviderVal({
            showPlaceHolderContent: false,
            targeting: dfpAdTargeting,
            adBridgPage: adUnitPage,
            adBridgPageNew: adUnitPageNew,
            pageType: AD_UNIT_PAGE_TYPE.LISTINGS_SEARCH_RESULT_PAGE,
            delayInit: isEmptyObj(dfpAdTargeting)
        });
    }, [dfpAdTargeting, isLoggedIn, pageNumber, adUnitPage, adUnitPageNew]);

    const sidebarRef = useRef<HTMLDivElement>(null);
    const stickyContainerRef = useRef<HTMLDivElement>(null);
    const [, sidebarHeight] = useContainerMeasurement(sidebarRef);
    const [stickyContainerHeight, setStickyContainerHeight] = useState('auto');
    const [disableSticky, setDisableSticky] = useState(false);

    useEffect(() => {
        const stickyContainerYPosition =
            stickyContainerRef.current?.offsetTop || 0;
        const numberOfSkyscraperAds = 2;
        const margin = 32;
        const verticalSpace = sidebarHeight - stickyContainerYPosition;

        setDisableSticky(
            verticalSpace <=
                (SKYSCRAPER_AD_HEIGHT + margin) * numberOfSkyscraperAds
        );
        setStickyContainerHeight(
            disableSticky ? 'auto' : Math.floor(verticalSpace / 2) + 'px'
        );
    }, [sidebarHeight, stickyContainerHeight, disableSticky]);

    const isForSale = pathname.match(/for-sale|new-homes/);

    const {
        showAuthModal,
        setShowAuthModal,
        defaultAlertFrequency,
        setDefaultAlertFrequency,
        showSaveThisSearchModal,
        setShowSaveThisSearchModal
    } = useSaveThisSearchModal();

    const trackSegmentEvent = useTrackSegmentEvent();
    const userTaxonomy = useUser();

    const container = useRef<HTMLDivElement>(null);

    return (
        <AdvertProvider value={adProviderVal}>
            <SearchAndFiltersWrapper>
                {isForSale ? <QuickFilters /> : null}

                <div
                    className={searchResultsWrapperStyle}
                    suppressHydrationWarning={true}
                    ref={container}
                >
                    <Box
                        display="flex"
                        // justifyContent='space-between'
                        pb="S400"
                        bpM100={{ pb: 0 }}
                        bpL100={{ display: 'none' }}
                    >
                        <SaveThisSearchModalWithAuth
                            {...{
                                showAuthModal,
                                setShowAuthModal,
                                defaultAlertFrequency,
                                setDefaultAlertFrequency,
                                showSaveThisSearchModal,
                                setShowSaveThisSearchModal,
                                container
                            }}
                        />

                        <ButtonLeft
                            category="tertiary"
                            fw
                            Icon={AlertMedium}
                            onClick={() => {
                                setDefaultAlertFrequency('0');

                                trackCreateEmailAlert();

                                trackSegmentEvent(
                                    'Click Email Alert',
                                    {
                                        ...ANALYTICS_BASE_TAXONOMY,
                                        ...(analyticsTaxonomy as unknown as Taxonomy),
                                        ...(updateUserTaxonomyKeys(
                                            userTaxonomy
                                        ) as unknown as UserTaxonomy),
                                        signedInStatus: isLoggedIn
                                            ? 'signed in'
                                            : 'signed out',
                                        location: analyticsTaxonomy?.areaName
                                    },
                                    undefined
                                );

                                if (!isLoggedIn) {
                                    setShowAuthModal(true);
                                } else {
                                    setShowSaveThisSearchModal(true);
                                }
                            }}
                        >
                            Create alert
                        </ButtonLeft>

                        <ButtonLeft
                            fw
                            category="tertiary"
                            Icon={SaveMedium}
                            onClick={() => {
                                setDefaultAlertFrequency('');

                                trackSaveThisSearch();

                                trackSegmentEvent(
                                    'Click Save Search',
                                    {
                                        ...ANALYTICS_BASE_TAXONOMY,
                                        ...(analyticsTaxonomy as unknown as Taxonomy),
                                        ...(updateUserTaxonomyKeys(
                                            userTaxonomy
                                        ) as unknown as UserTaxonomy),
                                        signedInStatus: isLoggedIn
                                            ? 'signed in'
                                            : 'signed out',
                                        location: analyticsTaxonomy?.areaName
                                    },
                                    undefined
                                );

                                if (!isLoggedIn) {
                                    setShowAuthModal(true);
                                } else {
                                    setShowSaveThisSearchModal(true);
                                }
                            }}
                        >
                            Save search
                        </ButtonLeft>
                    </Box>
                    <div className={innerWrapperStyle}>
                        <section
                            className={sectionStyle}
                            data-testid="search-content"
                        >
                            {children}
                        </section>
                        <aside
                            className={sideBarStyle}
                            aria-label="Search toolbar"
                            data-testid="search-sidebar"
                            ref={sidebarRef}
                        >
                            <AlertPanel analyticsTaxonomy={analyticsTaxonomy} />
                            <AdvertSlotDfp
                                uuid={'pn' + pageNumber + '_sb1'}
                                position={0}
                                showOnly={['>', 1140]}
                                sizes={[[300, 16], 'fluid']}
                                adUnitCode={[
                                    AD_UNIT_ROOTS.TEXT_LINK,
                                    getPageSectionForAdUnit(route),
                                    AD_UNIT_PAGE.LSRP
                                ]}
                                adbridgClass={ADBRIDG_CLASS.TEXT_LINK_LSRP}
                                adbridgDataClass={ADBRIDG_CLASS.ADB_TEXT_LINK}
                            />
                            <AdvertSlotDfp
                                uuid={'pn' + pageNumber + '_sb2'}
                                position={0}
                                showOnly={['>', 1140]}
                                sizes={[[300, 250]]}
                                adUnitCode={[AD_UNIT_ROOTS.MPU, adUnitPage]}
                                adbridgClass={ADBRIDG_CLASS.MPU}
                            />
                            <AdvertSlotDfp
                                uuid={'pn' + pageNumber + '_sb3'}
                                position={0}
                                showOnly={['>', 1140]}
                                sizes={[[300, 120], 'fluid']}
                                adUnitCode={DFP_AD_UNIT.VALUATION_BOOSTER}
                                adbridgClass={ADBRIDG_CLASS.VALUATION_BOOSTER}
                            />
                            <AdvertSlotDfp
                                uuid={'pn' + pageNumber + '_sb4'}
                                position={1}
                                showOnly={['>', 1140]}
                                sizes={[[300, 250]]}
                                adUnitCode={[AD_UNIT_ROOTS.MPU_2, adUnitPage]}
                                adbridgClass={ADBRIDG_CLASS.MPU_2}
                            />

                            {!!seoAccordions.length && <SeoLinksComponent />}

                            <div
                                id="adb_ss_target"
                                data-testid="adbridge-sidebar"
                            />
                        </aside>
                    </div>
                </div>

                <AlertCtaModal links={links} />
            </SearchAndFiltersWrapper>
        </AdvertProvider>
    );
};

export default SearchResultsLayout;
